import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useAuth from "../../auth/accessToken/hooks/useAuth";
import { isPartner } from "../../auth/helpers";
import { useAccountId } from "../../auth/useAccountId";
import MerchantLogo from "../../components/AppBar/MerchantLogo";
import { P } from "../../components/Typography";
import View from "../../components/View";
import useMemorizedState from "../../dashboard/hooks/useMemorizedState";
import {
    defaultRadius,
    distances,
    focusBoxShadow,
    palette,
    placeholderColor,
    shadows,
} from "../../styles/constants";
import type { AuthenticatedAccountUser } from "../../types/management-auth";
import { isRootAccount } from "../../util/accountId";
import { isMobile } from "../../util/dom";

type AccountListProps = {
    currentAccount?: AuthenticatedAccountUser["accounts"][number] | undefined;
    onChangeAccount: (
        account: AuthenticatedAccountUser["accounts"][number],
    ) => void;
};

type ProcessedAccount = AuthenticatedAccountUser["accounts"][number] & {
    visitedAt: Date | null;
    testAccount?: AuthenticatedAccountUser["accounts"][number];
    productionAccount?: AuthenticatedAccountUser["accounts"][number];
    originalAccountId: string;
};

const processAccounts = (
    accounts: AuthenticatedAccountUser["accounts"],
    accountId: string,
): ProcessedAccount[] => {
    const internalAccountId = accountId.substring(1);
    const reducedAccounts = accounts.reduce<ProcessedAccount[]>(
        (acc, account) => {
            const baseId = account.account_id.substring(1);
            const productionAccountId = `P${baseId}`;
            const testAccountId = `T${baseId}`;

            const alreadyProcessed = acc.some(
                (processedAccount) => processedAccount.account_id === baseId,
            );
            if (alreadyProcessed) {
                return acc;
            }

            const productionAccount = accounts.find(
                (account) => account.account_id === productionAccountId,
            );
            const testAccount = accounts.find(
                (a) => a.account_id === testAccountId,
            );

            acc.push({
                ...account,
                visitedAt: null,
                ...(productionAccount && { productionAccount }),
                ...(testAccount && { testAccount }),
                account_id: baseId,
                originalAccountId: account.account_id,
            });

            return acc;
        },
        [],
    );
    return reducedAccounts.filter(
        (account) => account.account_id !== internalAccountId,
    );
};

const gotoAccount = (
    isTestMode: boolean,
    accountId: string,
    account: ProcessedAccount,
    onChangeAccount: (
        account: AuthenticatedAccountUser["accounts"][number],
    ) => void,
) => {
    const targetAccount = isTestMode
        ? account.testAccount
        : account.productionAccount;
    const fallbackAccount = {
        ...account,
        account_id: account.originalAccountId,
    };
    // If neither test nor production account exists, try switching to the other mode's account_id
    if (!targetAccount) {
        fallbackAccount.account_id = isTestMode
            ? `P${account.account_id}`
            : `T${account.account_id}`;
    }
    onChangeAccount(targetAccount || fallbackAccount);
};

export default function AccountList({
    currentAccount,
    onChangeAccount,
}: AccountListProps) {
    const shouldAutoFocus = !isMobile();
    const { t } = useTranslation();
    const accountId = useAccountId();
    const [query, setQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const listRef = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const showOrgNumber =
        isRootAccount(currentAccount?.account_id) ||
        isPartner(currentAccount?.account_id || "");

    const _accounts = useAuth((state) => state.account_user.accounts);
    const allAccountsRaw = useMemo(() => {
        return processAccounts(_accounts, accountId);
    }, [_accounts, accountId]);
    const [recentAccountsState, setRecentAccounts] = useMemorizedState<
        ProcessedAccount[]
    >(
        () => allAccountsRaw.filter((account) => account.visitedAt),
        "recentAccounts",
    );
    const allAccounts = allAccountsRaw
        .filter((account) => {
            const searchTerm = query.toLowerCase();
            return [
                account.display_name,
                account.business_name,
                account.account_id,
                account.testAccount?.account_id,
                account.productionAccount?.account_id,
            ].some((field) => field?.toLowerCase().includes(searchTerm));
        })
        .filter(
            (account) =>
                !recentAccountsState.some(
                    (recent) => recent.account_id === account.account_id,
                ),
        );
    const recentAccounts = recentAccountsState
        .filter((account) => account.account_id !== currentAccount?.account_id)
        .filter((account) => {
            const searchTerm = query.toLowerCase();
            return [
                account.display_name,
                account.business_name,
                account.account_id,
                account.testAccount?.account_id,
                account.productionAccount?.account_id,
            ].some((field) => field?.toLowerCase().includes(searchTerm));
        });

    const selectableAccounts = useMemo(() => {
        return [...recentAccounts, ...allAccounts];
    }, [recentAccounts, allAccounts]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Keep the function memoized
    const handleChangeAccount = useCallback(
        (account: ProcessedAccount) => async () => {
            // Update the recent accounts list
            await setRecentAccounts((prev) => {
                const updated = prev.filter(
                    (a) => a.account_id !== account.account_id,
                );
                updated.unshift({
                    ...account,
                    visitedAt: new Date(),
                });
                return updated.slice(0, 5); // Keep only the 5 most recent accounts
            });
            if (query) {
                return gotoAccount(
                    query.startsWith("T"),
                    accountId,
                    account,
                    onChangeAccount,
                );
            }
            gotoAccount(
                accountId.startsWith("T"),
                accountId,
                account,
                onChangeAccount,
            );
        },
        [query],
    );

    useEffect(() => {
        if (query) {
            setHighlightedIndex(0);
        }
    }, [query]);

    const handleSearchInputKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                Math.min(prev + 1, selectableAccounts.length - 1),
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const selectedAccount = selectableAccounts[highlightedIndex];
            if (selectedAccount) {
                handleChangeAccount(selectedAccount)();
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            setQuery("");
            setHighlightedIndex(0);
        }
    };

    if (recentAccounts.length === 0 && allAccounts.length === 0 && !query) {
        // No other accounts to display
        return null;
    }

    return (
        <AccountListElement>
            <SearchInputElement
                ref={inputRef}
                onChange={(e) => setQuery(e.target.value)}
                value={query}
                placeholder={t("change_account.search_accounts_placeholder")}
                autoFocus={shouldAutoFocus}
                role="combobox"
                aria-expanded={selectableAccounts.length > 0}
                aria-haspopup="listbox"
                aria-controls="account-list"
                aria-activedescendant={
                    selectableAccounts[highlightedIndex]
                        ? `account-${selectableAccounts[highlightedIndex].account_id}`
                        : undefined
                }
                onKeyDown={handleSearchInputKeyDown}
            />
            <ListElement role="listbox" id="account-list" ref={listRef}>
                {recentAccounts.length > 0 && (
                    <>
                        <StickyListHeaderElement role="listitem">
                            {t("change_account.recently_used")}
                        </StickyListHeaderElement>
                        {recentAccounts.map((account, index) => (
                            <ListItem
                                key={account.account_id}
                                onClick={handleChangeAccount(account)}
                                as="button"
                                role="option"
                                aria-selected={index === highlightedIndex}
                                $highlighted={index === highlightedIndex}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                id={`account-${account.account_id}`}
                            >
                                <MerchantLogo
                                    src={account.icon_url}
                                    alt={
                                        account.display_name ||
                                        account.business_name ||
                                        account.account_id
                                    }
                                />
                                <section>
                                    <P
                                        variant="sm"
                                        fontWeight={600}
                                        color={palette.white}
                                    >
                                        {account.display_name ||
                                            account.public_business_name ||
                                            account.business_name}
                                        {showOrgNumber && (
                                            <>
                                                {" "}
                                                <P
                                                    variant="sm"
                                                    fontWeight={400}
                                                    color={palette.neutral[300]}
                                                    as="strong"
                                                >
                                                    -
                                                </P>{" "}
                                                <P
                                                    variant="sm"
                                                    fontWeight={400}
                                                    color={palette.neutral[300]}
                                                    as="strong"
                                                >
                                                    {
                                                        account.organization_number
                                                    }
                                                </P>
                                            </>
                                        )}
                                    </P>
                                    <P
                                        variant="xs"
                                        color={palette.neutral[300]}
                                        fontWeight={500}
                                    >
                                        {account.account_id}
                                    </P>
                                </section>
                                <SwitchIcon />
                            </ListItem>
                        ))}
                    </>
                )}
                <StickyListHeaderElement role="listitem">
                    {t("change_account.accounts")}
                </StickyListHeaderElement>
                {allAccounts.map((account, index) => {
                    const actualIndex = recentAccounts.length + index;
                    return (
                        <ListItem
                            key={account.account_id}
                            onClick={handleChangeAccount(account)}
                            as="button"
                            role="option"
                            aria-selected={actualIndex === highlightedIndex}
                            $highlighted={actualIndex === highlightedIndex}
                            onMouseEnter={() =>
                                setHighlightedIndex(actualIndex)
                            }
                            id={`account-${account.account_id}`}
                        >
                            <MerchantLogo
                                src={account.icon_url}
                                alt={
                                    account.display_name ||
                                    account.business_name ||
                                    account.account_id
                                }
                            />
                            <section>
                                <P
                                    variant="sm"
                                    fontWeight={600}
                                    color={palette.white}
                                >
                                    {account.display_name ||
                                        account.public_business_name ||
                                        account.business_name}
                                    {showOrgNumber && (
                                        <>
                                            {" "}
                                            <P
                                                variant="sm"
                                                fontWeight={400}
                                                color={palette.neutral[300]}
                                                as="strong"
                                            >
                                                -
                                            </P>{" "}
                                            <P
                                                variant="sm"
                                                fontWeight={400}
                                                color={palette.neutral[300]}
                                                as="strong"
                                            >
                                                {account.organization_number}
                                            </P>
                                        </>
                                    )}
                                </P>
                                <P
                                    variant="xs"
                                    color={palette.neutral[300]}
                                    fontWeight={500}
                                >
                                    {account.account_id}
                                </P>
                            </section>
                            <SwitchIcon />
                        </ListItem>
                    );
                })}
            </ListElement>
        </AccountListElement>
    );
}

export const SwitchIcon = forwardRef<SVGSVGElement>((props, ref) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        {...props}
        ref={ref}
    >
        <path
            d="M6.60156 4.49844C6.21496 4.49844 5.90156 4.81184 5.90156 5.19844C5.90156 5.58504 6.21496 5.89844 6.60156 5.89844L10.5116 5.89844L9.60659 6.80346C9.33322 7.07683 9.33322 7.52004 9.60659 7.79341C9.87995 8.06678 10.3232 8.06678 10.5965 7.79341L12.6965 5.69341C12.8278 5.56214 12.9016 5.38409 12.9016 5.19844C12.9016 5.01279 12.8278 4.83474 12.6965 4.70346L10.5965 2.60346C10.3232 2.3301 9.87996 2.3301 9.60659 2.60346C9.33322 2.87683 9.33322 3.32005 9.60659 3.59341L10.5116 4.49844L6.60156 4.49844Z"
            fill="currentColor"
        />
        <path
            d="M9.40156 11.4984C9.78816 11.4984 10.1016 11.185 10.1016 10.7984C10.1016 10.4118 9.78816 10.0984 9.40156 10.0984L5.49151 10.0984L6.39654 9.19341C6.6699 8.92005 6.6699 8.47683 6.39654 8.20346C6.12317 7.9301 5.67996 7.9301 5.40659 8.20346L3.30659 10.3035C3.17531 10.4347 3.10156 10.6128 3.10156 10.7984C3.10156 10.9841 3.17531 11.1621 3.30659 11.2934L5.40659 13.3934C5.67995 13.6668 6.12317 13.6668 6.39654 13.3934C6.6699 13.12 6.6699 12.6768 6.39654 12.4035L5.49151 11.4984L9.40156 11.4984Z"
            fill="currentColor"
        />
    </svg>
));

const ListItem = (props: Parameters<typeof ListItemElement>[0]) => {
    const elementRef = useRef<HTMLLIElement>(null);

    useEffect(() => {
        if (elementRef.current && props.$highlighted) {
            const isFirstElement =
                elementRef.current.previousElementSibling === null ||
                elementRef.current.previousElementSibling?.getAttribute(
                    "role",
                ) === "listitem";

            if (isFirstElement) {
                elementRef.current.closest("ul")?.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            } else {
                elementRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "start",
                });
            }
        }
    }, [props.$highlighted]);

    return <ListItemElement {...props} ref={elementRef} />;
};

const SearchInputElement = styled.input`
      display: flex;
      height: 40px;
      padding: 8px 12px;
      align-items: center;
      gap: 12px;
      align-self: stretch;
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      color: ${palette.white};
      margin: ${distances.tiny};
      transition: all 0.15s;

      border-radius: ${defaultRadius};
      border: 0px solid ${palette.neutral[400]};
      background: ${palette.darkmode[900]};

      box-shadow: ${shadows.xsmall};

      &::placeholder {
        color: ${placeholderColor};
        opacity: 1;
      }

      &:focus {
        outline: 2px solid ${palette.primary[300]};
        outline-offset: 2px;
        box-shadow: ${focusBoxShadow};
      }
  `;

const ListItemElement = styled.li<{ $highlighted?: boolean }>`
      background: unset;
      border: unset;
      text-align: left;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-inline: ${distances.tiny};
      padding: ${distances.tiny};
      gap: ${distances.tiny};
      color: ${palette.neutral[500]};
      border-radius: 4px;
      outline: 4px solid transparent;
      transition: all 0.15s;
      z-index: 1;
      background: ${({ $highlighted }) =>
          $highlighted ? palette.darkmode[50] : palette.darkmode[600]};
      cursor: pointer;
      section {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        flex: 1;
      }
      &:focus-visible {
        outline-color: ${palette.primary[100]};
        outline-offset: -2px;
        background: ${palette.darkmode[50]};
      }
      &:hover {
        background: ${palette.darkmode[50]};
      }
      `;

const AccountListElement = styled.div`
      display: flex;
      flex-direction: column;
      width: 100%;
    `;
const ListElement = styled.ul`
      display: flex;
      flex-direction: column;
      padding: 0;
      margin: 0;
      overflow: auto;
      max-height: 348px;
      padding-bottom: 4px;
      scrollbar-width: thin;
      scrollbar-color: ${palette.neutral[400]} transparent;
      `;

const StickyListHeaderElement = styled.li`
      position: sticky;
      top: 0;
      display: flex;
      padding: 2px 10px;
      align-items: center;
      gap: 6px;
      align-self: stretch;
      border-radius: 24px;
      background: ${palette.darkmode[300]};
      font-size: 12px;
      color: ${palette.neutral[300]};
      margin-inline: ${distances.tiny};
      `;
